// src/components/cryptcrawler/CryptCrawlerLanding.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Page = styled.div`
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #2a1414;
    background: linear-gradient(180deg, #FBEDED 0%, #FFFFFF 100%);
    min-height: 100vh;
`;

const Container = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 4rem 1.5rem;
`;

const Hero = styled.section`
    text-align: center;
    padding: 5rem 1rem 4rem;
`;

const Logo = styled.div`
    display: inline-block;
    background: #9B2C2C;
    color: white;
    font-weight: 900;
    font-size: 1rem;
    letter-spacing: 1px;
    padding: 0.4rem 1rem;
    border-radius: 999px;
    margin-bottom: 1.5rem;
`;

const Title = styled.h1`
    font-size: clamp(2.5rem, 6vw, 4.5rem);
    font-weight: 900;
    margin: 0 0 1rem;
    color: #9B2C2C;
    line-height: 1.1;
`;

const Subtitle = styled.p`
    font-size: clamp(1rem, 2.5vw, 1.25rem);
    color: #6b5050;
    max-width: 600px;
    margin: 0 auto 2.5rem;
    line-height: 1.6;
`;

const PlayBtn = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    background: #9B2C2C;
    color: white;
    padding: 0.9rem 2rem;
    border-radius: 999px;
    font-weight: 700;
    text-decoration: none;
    box-shadow: 0 8px 24px rgba(155, 44, 44, 0.3);
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 32px rgba(155, 44, 44, 0.4);
    }
`;

const Features = styled.section`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.5rem;
    margin: 4rem 0;
`;

const FeatureCard = styled.div`
    background: white;
    padding: 2rem 1.5rem;
    border-radius: 16px;
    text-align: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    transition: transform 0.2s;

    &:hover {
        transform: translateY(-4px);
    }

    .icon {
        font-size: 2.5rem;
        margin-bottom: 0.8rem;
    }

    h3 {
        font-size: 1.15rem;
        color: #9B2C2C;
        margin-bottom: 0.5rem;
    }

    p {
        font-size: 0.95rem;
        color: #6b5050;
        line-height: 1.5;
    }
`;

const InfoSection = styled.section`
    background: white;
    padding: 3rem 2rem;
    border-radius: 20px;
    margin: 3rem 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

    h2 {
        color: #9B2C2C;
        font-size: 1.75rem;
        margin-bottom: 1rem;
    }

    p {
        color: #6b5050;
        line-height: 1.7;
        margin-bottom: 0.8rem;
    }
`;

const LegalLinks = styled.section`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1.5rem;
    margin: 3rem 0 1rem;
    padding-top: 2rem;
    border-top: 1px solid #ecd9d9;

    a {
        color: #9B2C2C;
        text-decoration: none;
        font-weight: 600;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const Footer = styled.footer`
    text-align: center;
    padding: 2rem 1rem 1rem;
    color: #9a8484;
    font-size: 0.9rem;

    a {
        color: #9B2C2C;
    }
`;

const CryptCrawlerLanding = () => {
    const features = [
        { icon: '🗝️', title: 'Dungeon Crawling', desc: 'Descend through three acts of crypts and infernal depths, floor by floor.' },
        { icon: '🃏', title: 'Deck-Building Battles', desc: 'Draft cards each run and outplay enemies in turn-based card combat.' },
        { icon: '⚔️', title: 'Two Classes', desc: 'Bloodforged trades blood for power; Thornclad turns defense into damage.' },
        { icon: '🎁', title: 'Free to Start', desc: 'Free to start with optional rewarded ads and in-app purchases.' },
    ];

    return (
        <Page>
            <Container>
                <Hero>
                    <Logo>MINDWORLD GAMES</Logo>
                    <Title>CryptCrawler</Title>
                    <Subtitle>
                        A roguelike deck-building dungeon crawler. Build your deck, survive the crypt, and descend into the inferno one run at a time.
                    </Subtitle>
                    <PlayBtn href="https://play.google.com/store" target="_blank" rel="noopener noreferrer">
                        ▶ Get it on Google Play (Coming Soon)
                    </PlayBtn>
                </Hero>

                <Features>
                    {features.map((f) => (
                        <FeatureCard key={f.title}>
                            <div className="icon">{f.icon}</div>
                            <h3>{f.title}</h3>
                            <p>{f.desc}</p>
                        </FeatureCard>
                    ))}
                </Features>

                <InfoSection>
                    <h2>About CryptCrawler</h2>
                    <p>
                        CryptCrawler is a single-player roguelike deck-building game for mobile. Explore dungeon floors, fight through turn-based card battles, collect cards and relics, and grow stronger with every run through permanent unlocks.
                    </p>
                    <p>
                        Built by MindWorld, an indie studio making mobile games.
                    </p>
                </InfoSection>

                <InfoSection>
                    <h2>Privacy & Ads</h2>
                    <p>
                        CryptCrawler is supported by ads served through Google AdMob. We respect your privacy and comply with GDPR, CCPA, and Korean PIPA. You can manage advertising preferences any time in your device settings.
                    </p>
                    <p>
                        No account registration required. All game progress is stored locally on your device.
                    </p>
                </InfoSection>

                <LegalLinks>
                    <Link to="/cryptcrawler/privacy">Privacy Policy</Link>
                    <Link to="/cryptcrawler/terms">Terms of Service</Link>
                    <Link to="/cryptcrawler/account-deletion">Data Deletion</Link>
                    <a href="mailto:mind-wellness@naver.com">Contact Support</a>
                </LegalLinks>

                <Footer>
                    © {new Date().getFullYear()} MindWorld · CryptCrawler · <a href="mailto:mind-wellness@naver.com">mind-wellness@naver.com</a>
                </Footer>
            </Container>
        </Page>
    );
};

export default CryptCrawlerLanding;
